import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './GasDistributorEmEntregaConfigModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de configuração específico para a lista "Em Entrega"
 * Permite configurar notificação quando card é movido para esta lista
 */
export default function GasDistributorEmEntregaConfigModal({ list, onClose, onSave, projectId }) {
  const [loading, setLoading] = useState(false);
  const [grupoConfigurado, setGrupoConfigurado] = useState(null);
  const [notificacaoAtivada, setNotificacaoAtivada] = useState(false);
  const [mensagemPersonalizada, setMensagemPersonalizada] = useState('');
  const [notificarCliente, setNotificarCliente] = useState(false);
  const [mensagemCliente, setMensagemCliente] = useState('');

  // Carregar configurações do projeto (grupo)
  useEffect(() => {
    const loadProjectConfig = async () => {
      try {
        const effectiveProjectId = projectId || list?.project_id;
        
        if (!effectiveProjectId) {
          console.error('projectId não disponível');
          return;
        }

        // Buscar config do gas_distributor (grupo)
        const { data: config, error } = await supabase
          .from('gas_distributor_config')
          .select('grupo_whatsapp_id, grupo_whatsapp_nome')
          .eq('project_id', effectiveProjectId)
          .maybeSingle();

        if (error) {
          console.error('Erro ao buscar configuração do grupo:', error);
          return;
        }

        if (config?.grupo_whatsapp_id) {
          setGrupoConfigurado({
            id: config.grupo_whatsapp_id,
            nome: config.grupo_whatsapp_nome || 'Grupo configurado'
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };

    // Carregar configuração da lista
    const loadListConfig = async () => {
      try {
        const { data: listData, error } = await supabase
          .from('project_lists')
          .select('metadata')
          .eq('id', list.id)
          .maybeSingle();

        if (error) throw error;

        const metadata = listData?.metadata || {};
        setNotificacaoAtivada(metadata.delivery_notification?.enabled || false);
        setMensagemPersonalizada(metadata.delivery_notification?.message || '');
        setNotificarCliente(metadata.delivery_notification?.notify_client || false);
        setMensagemCliente(metadata.delivery_notification?.client_message || '');
      } catch (error) {
        console.error('Erro ao carregar configuração da lista:', error);
      }
    };

    loadProjectConfig();
    if (list?.id) {
      loadListConfig();
    }
  }, [projectId, list?.id, list?.project_id]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const metadata = list?.metadata || {};
      const updatedMetadata = {
        ...metadata,
        delivery_notification: {
          enabled: notificacaoAtivada,
          message: mensagemPersonalizada,
          notify_client: notificarCliente,
          client_message: mensagemCliente
        }
      };

      const { error } = await supabase
        .from('project_lists')
        .update({
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      alert('Configurações salvas com sucesso!');
      onSave({ ...list, metadata: updatedMetadata });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="em-entrega-modal-overlay" onClick={onClose}>
      <div className="em-entrega-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="em-entrega-modal-header">
          <div className="em-entrega-modal-title-section">
            <AppIcon name="local_shipping" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="em-entrega-modal-content">
          {/* Seção de Notificação para Grupo */}
          <div className="em-entrega-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="group" />
                <h4>Notificação Grupo WhatsApp</h4>
              </div>
            </div>

            {/* Grupo Configurado */}
            <div className="grupo-info-box">
              <div className="grupo-info-header">
                <AppIcon name="groups" className="grupo-icon" />
                <div className="grupo-info-content">
                  <span className="grupo-label">Grupo Configurado no Projeto</span>
                  {grupoConfigurado ? (
                    <>
                      <span className="grupo-nome">{grupoConfigurado.nome}</span>
                      <span className="grupo-id">{grupoConfigurado.id}</span>
                    </>
                  ) : (
                    <span className="grupo-nao-configurado">Nenhum grupo configurado</span>
                  )}
                </div>
              </div>
            </div>

            {/* Toggle de Ativação */}
            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={notificacaoAtivada}
                  onChange={(e) => setNotificacaoAtivada(e.target.checked)}
                  disabled={!grupoConfigurado}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {notificacaoAtivada 
                    ? '✅ Notificação ativada' 
                    : '❌ Notificação desativada'}
                </span>
              </label>
              <small className="toggle-description">
                {notificacaoAtivada 
                  ? 'Quando um card for movido para esta lista, uma mensagem será enviada automaticamente para o grupo'
                  : 'Nenhuma mensagem será enviada automaticamente'}
              </small>
            </div>

            {/* Mensagem Personalizada (opcional) */}
            {notificacaoAtivada && (
              <div className="mensagem-section">
                <label className="mensagem-label">Mensagem (opcional)</label>
                <textarea
                  value={mensagemPersonalizada}
                  onChange={(e) => setMensagemPersonalizada(e.target.value)}
                  placeholder="🚚 Saiu para Entrega&#10;&#10;👤 Entregador: {entregador}&#10;&#10;📍 Endereço: {endereco}&#10;&#10;🛒 Produto: {produto}&#10;&#10;📦 Quantidade: {quantidade}"
                  rows={8}
                  className="mensagem-textarea"
                />
                <small className="mensagem-hint">
                  Grupo: {'{cliente}'}, {'{endereco}'}, {'{produto}'}, {'{quantidade}'}, {'{entregador}'},{' '}
                  {'{clicou_nome}'}, {'{clicou_numero}'} — no painel, {'{clicou_nome}'} é o usuário logado e {'{clicou_numero}'} fica &quot;—&quot;; no botão do grupo, são quem clicou.
                </small>
              </div>
            )}
          </div>

          {/* Informações sobre a lista */}
          <div className="list-info-section">
            <AppIcon name="info" className="info-icon" />
            <p>
              Esta mensagem ao grupo é enviada ao arrastar o card para esta lista (Kanban) ou quando alguém clica em
              &quot;Saiu para Entrega&quot; no grupo. Use os placeholders: no painel, {'{entregador}'} e {'{clicou_nome}'} usam o usuário logado;
              no botão do grupo, {'{clicou_nome}'} e {'{clicou_numero}'} identificam quem clicou.
            </p>
          </div>

          {/* Seção de Notificação para Cliente */}
          <div className="em-entrega-section client-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="person" />
                <h4>Notificação para o Cliente</h4>
              </div>
            </div>

            {/* Toggle de Ativação para Cliente */}
            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={notificarCliente}
                  onChange={(e) => setNotificarCliente(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {notificarCliente 
                    ? '✅ Notificar cliente no privado' 
                    : '❌ Não notificar cliente'}
                </span>
              </label>
              <small className="toggle-description">
                {notificarCliente 
                  ? 'Uma mensagem será enviada no privado para o cliente informando que seu pedido saiu para entrega'
                  : 'Nenhuma mensagem será enviada para o cliente'}
              </small>
            </div>

            {/* Mensagem para Cliente (opcional) */}
            {notificarCliente && (
              <div className="mensagem-section">
                <label className="mensagem-label">Mensagem para Cliente (opcional)</label>
                <textarea
                  value={mensagemCliente}
                  onChange={(e) => setMensagemCliente(e.target.value)}
                  placeholder="🚚 Seu pedido saiu para entrega!&#10;&#10;⏱️ Chegará em aproximadamente {tempo_entrega} minutos.&#10;&#10;Por favor, fique atento para receber o entregador.&#10;&#10;Obrigado pela preferência! 🙏"
                  rows={6}
                  className="mensagem-textarea"
                />
                <small className="mensagem-hint">
                  Variáveis disponíveis: {'{tempo_entrega}'}, {'{cliente}'}, {'{produto}'}, {'{quantidade}'}
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="em-entrega-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </div>
    </div>
  );
}
